/**
 * subsystems.ts
 *
 * CONSOLIDATED SINGLE SOURCE OF TRUTH for all subsystem configuration:
 *  - Type definitions and interfaces
 *  - Template configurations for each subsystem type
 *  - Visual styling constants (colors, opacity, grid, positioning)
 *  - Deck ↔ face-label ↔ node-prefix mappings (shared with thermal-heatmap)
 *  - Canonical identity data for all subsystems in the default layout
 *  - Instance creation helpers and batch functions
 *
 * Previously split across:
 *  - SubsystemTypes.ts (deprecated)
 *  - subsystemTemplates.ts
 *  - subsystemConstants.ts
 *  - subsystemInstanceHelpers.ts
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Subsystem mounting location - maps to thermal designer faces and internal decks
 * Maps to thermal designer faces and internal decks:
 * - deck-0: +X face (bottom external)
 * - deck-1: INT1 (Internal deck 1 at Y = -5/6)
 * - deck-2: INT2 (Internal deck 2 at Y = +5/6)
 * - deck-3: -X face (top external)
 *
 * Note: The four side faces (+Y, -Y, +Z, -Z) are external faces
 * but are not represented in the SubsystemDeck type.
 */
export type SubsystemDeck = 'deck-0' | 'deck-1' | 'deck-2' | 'deck-3';

/**
 * Available subsystem types for satellite integration
 */
export type SubsystemType =
  | 'x-band'
  | 's-band'
  | 'reaction-wheel'
  | 'star-tracker'
  | 'propulsion-tank'
  | 'thruster'
  | 'avionics'
  | 'power-system'
  | 'battery-pack'
  | 'flex-core-avionics'
  | 'ais-module'
  | 'tcu'
  | 'pms'
  | 'idrss-sdr';

/**
 * Geometric primitive types for subsystem visualization
 */
export type SubsystemGeometry = 'box' | 'cylinder' | 'sphere';

/**
 * Subsystem dimensions (cm)
 */
export interface SubsystemDimensions {
  width: number;   // cm
  height: number;  // cm
  length: number;  // cm
}

/**
 * Subsystem rotation angles (degrees)
 */
export interface SubsystemRotation {
  x: number;  // degrees
  y: number;  // degrees
  z: number;  // degrees
}

/**
 * Visual properties for subsystem rendering
 */
export interface SubsystemVisual {
  geometry: SubsystemGeometry;
  color: string;
  opacity: number;
  inactiveColor?: string;
  inactiveOpacity?: number;
  borderOpacity?: number;
}

/**
 * Subsystem mount location properties
 */
export interface SubsystemMountLocation {
  deck: SubsystemDeck;
  nodeId: string;
  positionOffset?: {
    x: number;
    y: number;
    z: number;
  };
}

/**
 * Thermal properties for subsystems
 */
export interface SubsystemThermalProperties {
  powerDissipation: number;
  mass: number;
  specificHeat: number;
  thermalConductivity: number;
  operatingTempRange: {
    min: number;
    max: number;
  };
}

/**
 * Template definition for a subsystem type
 */
export interface SubsystemTemplate {
  type: SubsystemType;
  defaultLabel: string;
  defaultDimensions: SubsystemDimensions;
  defaultRotation?: SubsystemRotation;
  defaultPositionOffset?: { x: number; y: number; z: number };
  geometry: SubsystemGeometry;
  defaultThermal: SubsystemThermalProperties;
}

/**
 * Full subsystem instance with all properties
 */
export interface SubsystemInstance {
  id: string;
  type: SubsystemType;
  label: string;
  mountLocation: SubsystemMountLocation;
  dimensions: SubsystemDimensions;
  rotation: SubsystemRotation;
  visual: SubsystemVisual;
  enabled: boolean;
  thermal: SubsystemThermalProperties;
}

// ============================================================================
// TEMPLATE DEFINITIONS
// ============================================================================

/**
 * Template configurations for all subsystem types.
 * Defines unique properties for each subsystem (dimensions, color, geometry).
 *
 * TODO: Update each template with actual specifications:
 * - Accurate dimensions (width, height, length in cm)
 * - Appropriate geometry type (box, cylinder, sphere)
 * - Correct color coding
 * - Real thermal properties (power, mass, specific heat, etc.)
 */
export const SUBSYSTEM_TEMPLATES: Record<SubsystemType, SubsystemTemplate> = {
  "x-band": {
    type: "x-band",
    defaultLabel: "X-Band Module",
    defaultDimensions: { width: 100, height: 40, length: 80 },
    geometry: "box",
    defaultThermal: {
      powerDissipation: 28,
      mass: 2.5,
      specificHeat: 900,
      thermalConductivity: 200,
      operatingTempRange: { min: -20, max: 60 },
    },
  },

  "s-band": {
    type: "s-band",
    defaultLabel: "S-Band Module",
    defaultDimensions: { width: 60, height: 30, length: 80 },
    geometry: "box",
    defaultThermal: {
      powerDissipation: 40,
      mass: 0.4,
      specificHeat: 900,
      thermalConductivity: 200,
      operatingTempRange: { min: -20, max: 60 },
    },
  },

  "reaction-wheel": {
    type: "reaction-wheel",
    defaultLabel: "Reaction Wheel",
    defaultDimensions: { width: 50, height: 90, length: 90 },
    defaultRotation: { x: 0, y: 0, z: 0 },
    defaultPositionOffset: { x: 0, y: 0, z: 0 },
    geometry: "box",
    defaultThermal: {
      powerDissipation: 17.7,
      mass: 1.2,
      specificHeat: 900,
      thermalConductivity: 200,
      operatingTempRange: { min: -20, max: 60 },
    },
  },

  "star-tracker": {
    type: "star-tracker",
    defaultLabel: "Star Tracker",
    defaultDimensions: { width: 50, height: 40, length: 120 },
    geometry: "box",
    defaultThermal: {
      powerDissipation: 2.4,
      mass: 0.3,
      specificHeat: 900,
      thermalConductivity: 200,
      operatingTempRange: { min: -20, max: 50 },
    },
  },

  "propulsion-tank": {
    type: "propulsion-tank",
    defaultLabel: "Propulsion Tank",
    defaultDimensions: { width: 90, height: 140, length: 140 },
    geometry: "cylinder",
    defaultThermal: {
      powerDissipation: 150,
      mass: 2.0,
      specificHeat: 4186,
      thermalConductivity: 50,
      operatingTempRange: { min: -20, max: 50 },
    },
  },

  thruster: {
    type: "thruster",
    defaultLabel: "Thruster",
    defaultDimensions: { width: 80, height: 60, length: 3 },
    geometry: "cylinder",
    defaultThermal: {
      powerDissipation: 3,
      mass: 0.2,
      specificHeat: 900,
      thermalConductivity: 200,
      operatingTempRange: { min: -20, max: 50 },
    },
  },

  avionics: {
    type: "avionics",
    defaultLabel: "Avionics",
    defaultDimensions: { width: 100, height: 100, length: 100 },
    geometry: "box",
    defaultThermal: {
      powerDissipation: 44.1,
      mass: 1.0,
      specificHeat: 900,
      thermalConductivity: 200,
      operatingTempRange: { min: 0, max: 60 },
    },
  },

  "power-system": {
    type: "power-system",
    defaultLabel: "Power System",
    defaultDimensions: { width: 160, height: 120, length: 130 },
    geometry: "box",
    defaultThermal: {
      powerDissipation: 9.98,
      mass: 1.5,
      specificHeat: 900,
      thermalConductivity: 200,
      operatingTempRange: { min: 0, max: 60 },
    },
  },

  "battery-pack": {
    type: "battery-pack",
    defaultLabel: "Battery Pack",
    defaultDimensions: { width: 190, height: 100, length: 130 },
    geometry: "box",
    defaultThermal: {
      powerDissipation: 2.44,
      mass: 2.0,
      specificHeat: 1000,
      thermalConductivity: 150,
      operatingTempRange: { min: 5, max: 25 },
    },
  },

  "flex-core-avionics": {
    type: "flex-core-avionics",
    defaultLabel: "Flexcore",
    defaultDimensions: { width: 100, height: 60, length: 70 },
    geometry: "box",
    defaultThermal: {
      powerDissipation: 3.4,
      mass: 0.8,
      specificHeat: 900,
      thermalConductivity: 200,
      operatingTempRange: { min: 0, max: 60 },
    },
  },

  "ais-module": {
    type: "ais-module",
    defaultLabel: "AIS SDR",
    defaultDimensions: { width: 40, height: 120, length: 90 },
    geometry: "box",
    defaultThermal: {
      powerDissipation: 0.5,
      mass: 0.4,
      specificHeat: 900,
      thermalConductivity: 200,
      operatingTempRange: { min: -20, max: 60 },
    },
  },

  tcu: {
    type: "tcu",
    defaultLabel: "TCU (Thruster Control Unit)",
    defaultDimensions: { width: 70, height: 50, length: 60 },
    geometry: "box",
    defaultThermal: {
      powerDissipation: 19.7,
      mass: 0.3,
      specificHeat: 900,
      thermalConductivity: 200,
      operatingTempRange: { min: -20, max: 50 },
    },
  },

  pms: {
    type: "pms",
    defaultLabel: "PMS",
    defaultDimensions: { width: 50, height: 50, length: 60 },
    geometry: "box",
    defaultThermal: {
      powerDissipation: 1,
      mass: 0.5,
      specificHeat: 900,
      thermalConductivity: 200,
      operatingTempRange: { min: -20, max: 50 },
    },
  },

  "idrss-sdr": {
    type: "idrss-sdr",
    defaultLabel: "IDRSS SDR",
    defaultDimensions: { width: 90, height: 20, length: 120 },
    geometry: "box",
    defaultThermal: {
      powerDissipation: 4,
      mass: 1.0,
      specificHeat: 900,
      thermalConductivity: 200,
      operatingTempRange: { min: -20, max: 60 },
    },
  },
};

/**
 * Helper function to get a template by type
 */
export const getSubsystemTemplate = (type: SubsystemType): SubsystemTemplate => {
  return SUBSYSTEM_TEMPLATES[type];
};

// ============================================================================
// VISUAL STYLING CONSTANTS
// ============================================================================

/**
 * Subsystem colors (used when subsystem view is active)
 */
export const SUBSYSTEM_COLORS = {
  SELECTED: "#F2A93B",           // Orange - used when face/subsystem is selected
  SELECTED_EDGE: "#F2A93B",      // Edge color for selected state
  INACTIVE: "#69D5F3",           // Cyan - inactive subsystems
  INACTIVE_EDGE: "#69D5F3",      // Edge color for inactive state
} as const;

/**
 * Structural component colors (faces, cylinders, telescope)
 * Used in normal view mode
 */
export const STRUCTURAL_COLORS = {
  ACTIVE: "#FFA500",             // Orange - selected face/component
  ACTIVE_EDGE: "#FFA500",        // Edge color for active state
  INACTIVE: "#4B84A9",           // Blue - unselected face/component
  INACTIVE_EDGE: "#8BD0FC",      // Light blue - edge color for inactive state
} as const;

/**
 * Subsystem opacity values (used in subsystem view mode)
 */
export const SUBSYSTEM_OPACITY = {
  SELECTED: 0.4,                 // Fill opacity when selected
  SELECTED_EDGE: 0.7,            // Edge opacity when selected
  INACTIVE: 0.4,                 // Fill opacity when inactive
  INACTIVE_EDGE: 0.5,            // Edge opacity when inactive
  UNSELECTED_FACE: 0.05,         // Very transparent when face not selected
} as const;

/**
 * Structural component opacity values (used in normal view mode)
 */
export const STRUCTURAL_OPACITY = {
  ACTIVE_FILL: 0.2,              // Fill opacity for selected face/component
  ACTIVE_EDGE: 1.0,              // Edge opacity for selected face/component
  ACTIVE_DOT: 1.0,               // Dot opacity for selected nodes
  INACTIVE_FILL: 0.12,           // Fill opacity for unselected face/component (default)
  INACTIVE_FILL_ALT: 0.06,       // Alternate fill opacity (for BottomCylinder)
  INACTIVE_EDGE: 0.5,            // Edge opacity for unselected face/component (default)
  INACTIVE_EDGE_ALT: 0.4,        // Alternate edge opacity (for BottomCylinder)
  INACTIVE_DOT: 0.4,             // Dot opacity for unselected nodes
} as const;

/**
 * Default opacity for subsystem templates
 */
export const DEFAULT_SUBSYSTEM_OPACITY = 0.85;

/**
 * Grid configuration for thermal nodes
 */
export const THERMAL_GRID = {
  SPACING: 5 / 3,                // Grid spacing (1.667 units)
  OFFSET_CLEARANCE: 0.01,        // 1cm clearance from face
  DEFAULT_OFFSET: 0.05,          // 5cm default offset if dimensions unavailable
} as const;

/**
 * 3x3 grid positions for thermal nodes
 * Positions 0-8 map to grid coordinates in XZ or appropriate plane
 *
 * Grid layout:
 * 6  7  8
 * 3  4  5
 * 0  1  2
 */
export const GRID_POSITIONS: ReadonlyArray<readonly [number, number]> = [
  [-THERMAL_GRID.SPACING, -THERMAL_GRID.SPACING], // 0: bottom-left
  [0, -THERMAL_GRID.SPACING],                      // 1: bottom-center
  [THERMAL_GRID.SPACING, -THERMAL_GRID.SPACING],  // 2: bottom-right
  [-THERMAL_GRID.SPACING, 0],                      // 3: middle-left
  [0, 0],                                          // 4: center
  [THERMAL_GRID.SPACING, 0],                       // 5: middle-right
  [-THERMAL_GRID.SPACING, THERMAL_GRID.SPACING],  // 6: top-left
  [0, THERMAL_GRID.SPACING],                       // 7: top-center
  [THERMAL_GRID.SPACING, THERMAL_GRID.SPACING],   // 8: top-right
] as const;

/**
 * Internal deck Y position (5/6 ≈ 0.833)
 */
const DECK_Y = 5 / 6;

/**
 * Face positions in 3D space
 */
export const FACE_POSITIONS = {
  "deck-0": [0, -2.501, 0] as const,  // +X face (bottom external)
  "deck-1": [0, -DECK_Y, 0] as const, // INT1 (Internal deck 1 at Y = -5/6)
  "deck-2": [0, DECK_Y, 0] as const,  // INT2 (Internal deck 2 at Y = +5/6)
  "deck-3": [0, 2.501, 0] as const,   // -X face (top external)
  "+Y": [2.501, 0, 0] as const,       // Right face (external)
  "-Y": [-2.501, 0, 0] as const,      // Left face (external)
  "+Z": [0, 0, 2.501] as const,       // Front face (external)
  "-Z": [0, 0, -2.501] as const,      // Back face (external)
} as const;

/**
 * Maps thermal faces to deck identifiers
 * Used for filtering which subsystems to display based on selected face
 */
export const FACE_TO_DECK_MAP = {
  "+X": "deck-0",
  "-X": "deck-3",
  "INT1": "deck-1",  // Internal deck 1
  "INT2": "deck-2",  // Internal deck 2
  "+Y": "+Y",
  "-Y": "-Y",
  "+Z": "+Z",
  "-Z": "-Z",
} as const;

/**
 * Human-oriented face label for each deck
 */
export const DECK_TO_FACE_LABEL = {
  "deck-0": "+X",
  "deck-1": "INT1",
  "deck-2": "INT2",
  "deck-3": "-X",
} as const;

/**
 * Thermal model node-key prefix for each deck (used to build keys like "PX_N3")
 */
export const DECK_TO_NODE_PREFIX = {
  "deck-0": "PX",
  "deck-1": "INT1",
  "deck-2": "INT2",
  "deck-3": "NX",
} as const;

export type DeckId = keyof typeof DECK_TO_NODE_PREFIX;
export type DeckNodePrefix = (typeof DECK_TO_NODE_PREFIX)[DeckId];

/**
 * Reverse lookup: node-key prefix → deck id
 */
export const NODE_PREFIX_TO_DECK = Object.fromEntries(
  (Object.entries(DECK_TO_NODE_PREFIX) as [DeckId, DeckNodePrefix][]).map(
    ([deck, prefix]) => [prefix, deck]
  )
) as Record<DeckNodePrefix, DeckId>;

/**
 * Convert centimeters to meters for Three.js coordinate system
 */
export const CM_TO_M = 0.01;

/**
 * Angle threshold for edge detection in degrees
 * Lower values create more edges
 */
export const EDGE_ANGLE_THRESHOLD = 15;

// ============================================================================
// LAYOUT DATA - Canonical subsystem identity
// ============================================================================

export interface SubsystemLayoutEntry {
  type: SubsystemType;
  label: string;
  deck: SubsystemDeck;
  nodeId: string;
}

/**
 * Canonical identity data (type/label/deck/nodeId) for every subsystem in the
 * default satellite layout. Shared by:
 *  - ThermalDesigner's DEFAULT_SUBSYSTEM_PLACEMENTS (3D Designer scene defaults)
 *  - thermal-heatmap's THERMAL_HEATMAP_SUBSYSTEM_PLACEMENTS (2D flattened fallback layout)
 *
 * Each consumer merges its own context-specific overrides (rotation, positionOffset,
 * shape, dimensionOverride) on top, keyed by `label` (labels are unique across all entries).
 * Do NOT add rotation/positionOffset/shape/dimensionOverride here — those legitimately
 * differ per consumer (3D Designer scene vs. flattened heatmap face).
 */
export const SUBSYSTEM_LAYOUT: readonly SubsystemLayoutEntry[] = [
  { type: "x-band", label: "X-Band Module #1", deck: "deck-0", nodeId: "1-8" },
  { type: "x-band", label: "X-Band Module #2", deck: "deck-0", nodeId: "1-6" },
  { type: "s-band", label: "S-Band Module #1", deck: "deck-0", nodeId: "1-0" },
  { type: "s-band", label: "S-Band Module #2", deck: "deck-0", nodeId: "1-2" },
  { type: "reaction-wheel", label: "Reaction Wheel #1", deck: "deck-1", nodeId: "INT1-0" },
  { type: "reaction-wheel", label: "Reaction Wheel #2", deck: "deck-1", nodeId: "INT1-2" },
  { type: "reaction-wheel", label: "Reaction Wheel #3", deck: "deck-1", nodeId: "INT1-8" },
  { type: "reaction-wheel", label: "Reaction Wheel #4", deck: "deck-1", nodeId: "INT1-6" },
  { type: "star-tracker", label: "Star Tracker #1", deck: "deck-1", nodeId: "INT1-7" },
  { type: "star-tracker", label: "Star Tracker #2", deck: "deck-1", nodeId: "INT1-7" },
  { type: "avionics", label: "Avionics Module", deck: "deck-2", nodeId: "INT2-5" },
  { type: "power-system", label: "Power System Module", deck: "deck-2", nodeId: "INT2-2" },
  { type: "battery-pack", label: "Battery Pack", deck: "deck-2", nodeId: "INT2-1" },
  { type: "flex-core-avionics", label: "Flex Core Avionics", deck: "deck-2", nodeId: "INT2-3" },
  { type: "ais-module", label: "AIS Module", deck: "deck-2", nodeId: "INT2-0" },
  { type: "propulsion-tank", label: "Propulsion Tank", deck: "deck-2", nodeId: "INT2-8" },
  { type: "thruster", label: "Thruster", deck: "deck-2", nodeId: "INT2-7" },
  { type: "tcu", label: "TCU", deck: "deck-2", nodeId: "INT2-6" },
  { type: "pms", label: "PMS", deck: "deck-2", nodeId: "INT2-6" },
  { type: "idrss-sdr", label: "IDRSS SDR", deck: "deck-3", nodeId: "2-4" },
] as const;

// ============================================================================
// INSTANCE CREATION HELPERS
// ============================================================================

/**
 * Default deck for subsystem placement
 */
const DEFAULT_DECK: SubsystemDeck = "deck-0";

/**
 * Default node ID for subsystem placement (center of face)
 */
const DEFAULT_NODE_ID = "1-4";

/**
 * Options for creating a subsystem instance
 */
export interface CreateSubsystemInstanceOptions {
  label?: string;
  mountLocation?: {
    deck: SubsystemDeck;
    nodeId: string;
    positionOffset?: { x: number; y: number; z: number };
  };
  rotation?: Partial<SubsystemRotation>;
  dimensions?: Partial<SubsystemDimensions>;
  enabled?: boolean;
}

/**
 * Generate a unique ID for a subsystem instance
 */
const generateInstanceId = (type: SubsystemType): string => {
  return `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Create a new subsystem instance from a template
 */
export const createSubsystemInstance = (type: SubsystemType, options?: CreateSubsystemInstanceOptions): SubsystemInstance => {
  const template = getSubsystemTemplate(type);
  const id = generateInstanceId(type);

  return {
    id,
    type: template.type,
    label: options?.label || template.defaultLabel,
    mountLocation: {
      deck: options?.mountLocation?.deck || DEFAULT_DECK,
      nodeId: options?.mountLocation?.nodeId || DEFAULT_NODE_ID,
      positionOffset: options?.mountLocation?.positionOffset || template.defaultPositionOffset || { x: 0, y: 0, z: 0 },
    },
    dimensions: {
      ...template.defaultDimensions,
      ...options?.dimensions,
    },
    rotation: {
      ...(template.defaultRotation || { x: 0, y: 0, z: 0 }),
      ...options?.rotation,
    },
    visual: {
      geometry: template.geometry,
      color: "#69D5F3",
      opacity: 0.85,
      inactiveColor: "#69D5F3",
      inactiveOpacity: 0.4,
      borderOpacity: 0.7,
    },
    enabled: options?.enabled !== undefined ? options.enabled : true,
    thermal: { ...template.defaultThermal },
  };
};

/**
 * Configuration for batch subsystem creation
 */
interface SubsystemPlacement {
  type: SubsystemType;
  label: string;
  deck: SubsystemDeck;
  nodeId: string;
  rotation?: { x?: number; y?: number; z?: number };
  positionOffset?: { x?: number; y?: number; z?: number };
}

/**
 * Create multiple subsystem instances from a configuration array
 */
export const createSubsystemBatch = (placements: SubsystemPlacement[]): SubsystemInstance[] => {
  return placements.map((placement) =>
    createSubsystemInstance(placement.type, {
      label: placement.label,
      rotation: placement.rotation ? { x: placement.rotation.x ?? 0, y: placement.rotation.y ?? 0, z: placement.rotation.z ?? 0 } : undefined,
      mountLocation: {
        deck: placement.deck,
        nodeId: placement.nodeId,
        positionOffset: placement.positionOffset ? { x: placement.positionOffset.x ?? 0, y: placement.positionOffset.y ?? 0, z: placement.positionOffset.z ?? 0 } : undefined,
      },
    }),
  );
};

/**
 * Designer-specific rotation/positionOffset overrides for subsystems
 */
interface DesignerPlacementOverride {
  rotation?: { x?: number; y?: number; z?: number };
  positionOffset?: { x?: number; y?: number; z?: number };
}

const DESIGNER_PLACEMENT_OVERRIDES: Record<string, DesignerPlacementOverride> = {
  "X-Band Module #1": { positionOffset: { x: -40, y: 0, z: 20 } },
  "X-Band Module #2": { positionOffset: { x: 40, y: 0, z: 20 } },
  "S-Band Module #1": { positionOffset: { x: 0, y: 0, z: 0 } },
  "S-Band Module #2": { positionOffset: { x: 0, y: 0, z: 0 } },
  "Reaction Wheel #1": { rotation: { x: 0, y: 45, z: 0 } },
  "Reaction Wheel #2": { rotation: { x: 0, y: 135, z: 0 } },
  "Reaction Wheel #3": { rotation: { x: 0, y: 225, z: 0 } },
  "Reaction Wheel #4": { rotation: { x: 0, y: 315, z: 0 } },
  "Star Tracker #1": { rotation: { x: 0, y: 45, z: 0 }, positionOffset: { x: 90, y: 0, z: 80 } },
  "Star Tracker #2": { rotation: { x: 0, y: 135, z: 0 }, positionOffset: { x: -90, y: 0, z: 80 } },
  "Power System": { positionOffset: { x: -20, y: 0, z: 0 } },
  "Battery Pack": { positionOffset: { x: -60, y: 0, z: 0 } },
  "AIS SDR": { positionOffset: { x: -20, y: 0, z: -20 } },
  "Propulsion Tank": { rotation: { x: 90, y: 0, z: 90 }, positionOffset: { x: -30, y: -25, z: 0 } },
  "Thruster": { rotation: { x: 270, y: 0, z: 0 }, positionOffset: { x: 0, y: 5, z: 100 } },
  "TCU": { positionOffset: { x: -30, y: 0, z: 0 } },
  "PMS": { positionOffset: { x: 50, y: 0, z: 20 } },
};

/**
 * Default subsystem placements for initial Designer scene setup
 */
export const DEFAULT_SUBSYSTEM_PLACEMENTS: SubsystemPlacement[] = SUBSYSTEM_LAYOUT.map((entry) => ({
  ...entry,
  ...DESIGNER_PLACEMENT_OVERRIDES[entry.label],
}));

/**
 * Create all default subsystem instances for initial scene setup
 */
export const createAllDefaultSubsystemInstances = (): SubsystemInstance[] => {
  return createSubsystemBatch(DEFAULT_SUBSYSTEM_PLACEMENTS);
};

// ============================================================================
// UTILITY FUNCTIONS - Position, Dimension & Rotation Conversions
// ============================================================================

/**
 * Parse thermal node ID to extract face and grid position
 * @param nodeId - Format: "1-0" (faceNode-gridPosition) or "1" (single node)
 * @returns Grid position index (0-8), defaults to 4 (center) if not specified
 */
export const parseNodeId = (nodeId: string): number => {
  const parts = nodeId.split("-");
  return parts.length > 1 ? parseInt(parts[1], 10) : 4; // Default to center
};

/**
 * Get grid coordinates for a given grid position
 * @param gridPos - Grid position index (0-8)
 * @returns [x, z] coordinates in the grid plane
 */
export const getGridCoordinates = (gridPos: number): readonly [number, number] => {
  return GRID_POSITIONS[gridPos] || [0, 0];
};

/**
 * Calculate the offset distance from face based on subsystem dimensions
 * Subsystems are positioned "above" (inward from) the face surface
 * @param dimensions - Subsystem dimensions in centimeters
 * @returns Offset distance in meters
 */
export const calculateOffsetFromFace = (
  dimensions?: SubsystemDimensions
): number => {
  const heightMeters = dimensions
    ? dimensions.height * CM_TO_M
    : THERMAL_GRID.DEFAULT_OFFSET;
  return heightMeters / 2 + THERMAL_GRID.OFFSET_CLEARANCE;
};

/**
 * Determine offset direction vector for a given deck
 * Returns the unit vector pointing "inward" (toward satellite center)
 * For internal decks (deck-1, deck-2), offset upward (+Y) to place above deck
 * @param deck - Deck identifier
 * @returns Direction multiplier for each axis [x, y, z]
 */
export const getOffsetDirection = (
  deck: string
): readonly [number, number, number] => {
  switch (deck) {
    case "deck-0": // +X face (bottom): offset +Y (inward)
      return [0, 1, 0];
    case "deck-1": // INT1 (internal deck): offset +Y (place above deck)
      return [0, 1, 0];
    case "deck-2": // INT2 (internal deck): offset +Y (place above deck)
      return [0, 1, 0];
    case "deck-3": // -X face (top): offset -Y (inward)
      return [0, -1, 0];
    case "+Y": // Right face: offset -X (inward)
      return [-1, 0, 0];
    case "-Y": // Left face: offset +X (inward)
      return [1, 0, 0];
    case "+Z": // Front face: offset -Z (inward)
      return [0, 0, -1];
    case "-Z": // Back face: offset +Z (inward)
      return [0, 0, 1];
    default:
      return [0, 0, 0];
  }
};

/**
 * Apply grid coordinates to the correct plane based on deck orientation
 * @param deck - Deck identifier
 * @param gridX - Grid X coordinate
 * @param gridZ - Grid Z coordinate
 * @returns [x, y, z] position adjustment for the grid
 */
export const applyGridToPlane = (
  deck: string,
  gridX: number,
  gridZ: number
): readonly [number, number, number] => {
  if (deck === "deck-0" || deck === "deck-1" || deck === "deck-2" || deck === "deck-3") {
    // XZ plane (horizontal faces and internal decks)
    return [gridX, 0, gridZ];
  } else if (deck === "+Y" || deck === "-Y") {
    // YZ plane (vertical faces on Y axis)
    return [0, gridZ, 0];
  } else if (deck === "+Z" || deck === "-Z") {
    // XY plane (vertical faces on Z axis)
    return [gridX, gridZ, 0];
  }
  return [0, 0, 0];
};

/**
 * Calculate complete 3D position for a subsystem instance
 * @param nodeId - Thermal node ID (e.g., "1-0")
 * @param deck - Deck identifier (e.g., "deck-0")
 * @param dimensions - Optional subsystem dimensions for offset calculation
 * @param positionOffset - Optional position offset from node center (in cm)
 * @returns [x, y, z] position in Three.js world space
 */
export const calculateSubsystemPosition = (
  nodeId: string,
  deck: string,
  dimensions?: SubsystemDimensions,
  positionOffset?: { x: number; y: number; z: number }
): [number, number, number] => {
  // Get base position for the face/deck
  const basePos = FACE_POSITIONS[deck as keyof typeof FACE_POSITIONS] || [0, 0, 0];

  // Parse grid position from node ID
  const gridPos = parseNodeId(nodeId);
  const [gridX, gridZ] = getGridCoordinates(gridPos);

  // Apply grid coordinates to the appropriate plane
  const [gridOffsetX, gridOffsetY, gridOffsetZ] = applyGridToPlane(deck, gridX, gridZ);

  // Calculate offset from face surface
  const offset = calculateOffsetFromFace(dimensions);
  const [dirX, dirY, dirZ] = getOffsetDirection(deck);

  // Convert position offset from cm to meters
  const offsetX = (positionOffset?.x || 0) * CM_TO_M;
  const offsetY = (positionOffset?.y || 0) * CM_TO_M;
  const offsetZ = (positionOffset?.z || 0) * CM_TO_M;

  // Combine all components
  return [
    basePos[0] + gridOffsetX + dirX * offset + offsetX,
    basePos[1] + gridOffsetY + dirY * offset + offsetY,
    basePos[2] + gridOffsetZ + dirZ * offset + offsetZ,
  ];
};

/**
 * Convert subsystem dimensions from centimeters to meters
 * @param dimensions - Dimensions in centimeters
 * @returns Dimensions in meters for Three.js
 */
export const convertDimensionsToMeters = (
  dimensions: SubsystemDimensions
): { width: number; height: number; length: number } => {
  return {
    width: dimensions.width * CM_TO_M,
    height: dimensions.height * CM_TO_M,
    length: dimensions.length * CM_TO_M,
  };
};

/**
 * Convert rotation from degrees to radians for Three.js
 * @param degrees - Rotation in degrees
 * @returns Rotation in radians
 */
export const degreesToRadians = (degrees: number): number => {
  return (degrees * Math.PI) / 180;
};

/**
 * Convert rotation object from degrees to radians
 * @param rotation - Rotation in degrees {x, y, z}
 * @returns Rotation in radians [x, y, z]
 */
export const convertRotationToRadians = (rotation: {
  x: number;
  y: number;
  z: number;
}): [number, number, number] => {
  return [
    degreesToRadians(rotation.x),
    degreesToRadians(rotation.y),
    degreesToRadians(rotation.z),
  ];
};
